/**
 * The Prii mark, petal by petal (Stage 4, 2026-10-20). The mark is split into its nine petals and the centre dot
 * (src/assets/brand/mark-parts, made from brand/prii-mark-master.png; each layer is the full mark's size, so they stack
 * back into the mark exactly). One sentence, Sam's words: the petals load clockwise from 12 o'clock; while the app is not
 * ready they keep chasing round like a spinner; once it is, the lap finishes, the centre dot lands as the full stop with
 * a little glow, and onFinished fires (the launch then flies the mark to the header).
 *
 * Web Animations on opacity and transform only, so it keeps moving on iOS while the main thread is busy (a map build).
 * `variant` picks how a petal arrives and how the chase looks (VARIANTS); `ready` lets it finish.
 */
import { useEffect, useRef } from 'react';
import parts from '../../assets/brand/mark-parts/mark-parts.json';
import dot from '../../assets/brand/mark-parts/dot.png';
import p0 from '../../assets/brand/mark-parts/petal-0.png';
import p1 from '../../assets/brand/mark-parts/petal-1.png';
import p2 from '../../assets/brand/mark-parts/petal-2.png';
import p3 from '../../assets/brand/mark-parts/petal-3.png';
import p4 from '../../assets/brand/mark-parts/petal-4.png';
import p5 from '../../assets/brand/mark-parts/petal-5.png';
import p6 from '../../assets/brand/mark-parts/petal-6.png';
import p7 from '../../assets/brand/mark-parts/petal-7.png';
import p8 from '../../assets/brand/mark-parts/petal-8.png';
import './PetalMark.css';

export const PETALS = [p0, p1, p2, p3, p4, p5, p6, p7, p8];
export { dot as DOT, parts as MARK_PARTS };
const [CX, CY] = parts.center;
/** Each petal's pull toward the centre (fractions of the mark), for arrivals that come out of the middle. */
const IN = parts.petals.map((p) => [CX - p.cx, CY - p.cy]);
const GLOW = 'drop-shadow(0 0 14px rgba(201, 120, 255, 0.9))';
const GLOW_SOFT = 'drop-shadow(0 0 5px rgba(201, 120, 255, 0.45))';
const NO_GLOW = 'drop-shadow(0 0 0 rgba(201, 120, 255, 0))';
const OUT = 'cubic-bezier(.2,.8,.2,1)';

/**
 * How a petal arrives (build) and how the chase runs while waiting (pulse, every `step` ms round the nine; `rest` is a
 * waiting petal's opacity). size: the mark's px, for moves in px.
 */
export const VARIANTS = {
  fade: {
    label: 'Fade in',
    note: 'Each petal fades up in place; a brightness wave chases round.',
    build: () => [
      { opacity: 0, transform: 'scale(.72)' },
      { opacity: 1, transform: 'scale(1)' },
    ],
    buildMs: 300,
    gap: 85,
    rest: 0.42,
    pulse: () => [{ opacity: 0.42 }, { opacity: 1, offset: 0.3 }, { opacity: 0.42 }],
    pulseMs: 640,
    step: 95,
  },
  slide: {
    label: 'Slide out',
    note: 'Each petal slides out from the centre; the chase lifts each one a touch.',
    build: (i, s) => [
      {
        opacity: 0,
        transform: `translate(${IN[i][0] * s * 0.55}px, ${IN[i][1] * s * 0.55}px) scale(.6)`,
      },
      { opacity: 1, transform: 'none' },
    ],
    buildMs: 380,
    gap: 85,
    rest: 0.5,
    pulse: () => [
      { opacity: 0.5, transform: 'scale(1)' },
      { opacity: 1, transform: 'scale(1.08)', offset: 0.3 },
      { opacity: 0.5, transform: 'scale(1)' },
    ],
    pulseMs: 680,
    step: 95,
  },
  spring: {
    label: 'Spring',
    note: 'Petals pop in clockwise, then straight to the glow and the name. Still loading: the full mark breathes softly.',
    hold: true,
    build: () => [
      { opacity: 0, transform: 'scale(0)' },
      { opacity: 1, transform: 'scale(1.18)', offset: 0.6 },
      { opacity: 1, transform: 'scale(1)' },
    ],
    buildMs: 420,
    gap: 90,
    rest: 0.55,
    pulse: (i, s) => [
      { opacity: 0.55, transform: 'none' },
      {
        opacity: 1,
        transform: `translate(${-IN[i][0] * s * 0.06}px, ${-IN[i][1] * s * 0.06}px)`,
        offset: 0.3,
      },
      { opacity: 0.55, transform: 'none' },
    ],
    pulseMs: 700,
    step: 100,
  },
  glow: {
    label: 'Glow arrival',
    note: 'Each petal arrives with a flash of its glow; a glowing head chases round.',
    build: () => [
      { opacity: 0, filter: GLOW },
      { opacity: 1, filter: GLOW, offset: 0.4 },
      { opacity: 1, filter: NO_GLOW },
    ],
    buildMs: 520,
    gap: 85,
    rest: 0.6,
    pulse: () => [
      { opacity: 0.6, filter: NO_GLOW },
      { opacity: 1, filter: GLOW, offset: 0.25 },
      { opacity: 0.6, filter: NO_GLOW },
    ],
    pulseMs: 760,
    step: 100,
  },
  spinner: {
    label: 'Spinner',
    note: 'Petals switch on faint, clockwise; a bright head with a fading tail spins like a loader.',
    build: () => [{ opacity: 0 }, { opacity: 0.28 }],
    buildMs: 160,
    gap: 70,
    rest: 0.28,
    startDim: true,
    pulse: () => [{ opacity: 1 }, { opacity: 0.28 }],
    pulseMs: 520,
    step: 80,
  },
  springPace: {
    label: 'Spring, paced',
    note: 'Petals pop in clockwise; still loading, the last few slow down and the last one waits for the app.',
    paced: true,
    // Quicker (2026-10-30, the launch audit: the app was ready at ~0.4 s and this ran ~2.8 s more): petals 60 ms apart,
    // and `quick` hands off as the dot lands while its glow plays on through the fly to the header.
    build: () => [
      { opacity: 0, transform: 'scale(0)' },
      { opacity: 1, transform: 'scale(1.18)', offset: 0.6 },
      { opacity: 1, transform: 'scale(1)' },
    ],
    buildMs: 360,
    gap: 60,
    rest: 1,
    quick: true,
    pulse: () => [{ opacity: 1 }],
    pulseMs: 1,
    step: 60,
  },
  springName: {
    label: 'Spring, name early',
    note: 'The name is already under the mark while the petals pop in; the dot and glow land when ready.',
    hold: true,
    nameEarly: true,
    // Quicker (2026-10-30, the launch audit: the app was ready at ~0.4 s and this ran ~2.8 s more): petals 60 ms apart,
    // and `quick` hands off as the dot lands while its glow plays on through the fly to the header.
    build: () => [
      { opacity: 0, transform: 'scale(0)' },
      { opacity: 1, transform: 'scale(1.18)', offset: 0.6 },
      { opacity: 1, transform: 'scale(1)' },
    ],
    buildMs: 360,
    gap: 60,
    rest: 1,
    quick: true,
    pulse: () => [{ opacity: 1 }],
    pulseMs: 1,
    step: 60,
  },
  spinnerNow: {
    label: 'Spinner at once',
    note: 'Every petal is there faint from the first frame and the head is already spinning.',
    build: () => [{ opacity: 0 }, { opacity: 0.28 }],
    buildMs: 150,
    gap: 0,
    rest: 0.28,
    startDim: true,
    pulse: () => [{ opacity: 1 }, { opacity: 0.28 }],
    pulseMs: 520,
    step: 80,
  },
  spinnerQuick: {
    label: 'Spinner, quick start',
    note: 'The faint petals sweep in fast (a quarter of a second), then the head spins.',
    build: () => [{ opacity: 0 }, { opacity: 0.28 }],
    buildMs: 120,
    gap: 22,
    rest: 0.28,
    startDim: true,
    pulse: () => [{ opacity: 1 }, { opacity: 0.28 }],
    pulseMs: 520,
    step: 80,
  },
  comet3: {
    label: 'Three going round',
    note: 'Three petals at a time travel round; ready, it fills to the top and the dot lands.',
    window: 3,
    step: 115,
  },
  comet4: {
    label: 'Four going round',
    note: 'Four petals at a time, a little quicker; ready, it fills to the top and the dot lands.',
    window: 4,
    step: 95,
  },
  cometGrow: {
    label: 'Growing round',
    note: 'Three petals going round, one more each lap; ready, it fills to the top and the dot lands.',
    window: 3,
    grow: true,
    step: 105,
  },
  lap: {
    label: 'Lap',
    note: 'The circle fills petal by petal; still loading, it clears and fills again; ready, the dot lands.',
    build: () => [
      { opacity: 0, transform: 'scale(.7)' },
      { opacity: 1, transform: 'scale(1)' },
    ],
    buildMs: 240,
    gap: 70,
    rest: 1,
    relap: true,
    pulse: () => [{ opacity: 1 }],
    pulseMs: 1,
    step: 70,
  },
  grow: {
    label: 'Grow from the base',
    note: 'Each petal grows out of its base; while waiting, one petal at a time dips.',
    build: (i) => [
      { opacity: 0, transform: 'scale(.25)', transformOrigin: originAt(i, 0.62) },
      { opacity: 1, transform: 'scale(1)', transformOrigin: originAt(i, 0.62) },
    ],
    buildMs: 340,
    gap: 85,
    rest: 1,
    pulse: () => [
      { opacity: 1, transform: 'scale(1)' },
      { opacity: 0.35, transform: 'scale(.9)', offset: 0.35 },
      { opacity: 1, transform: 'scale(1)' },
    ],
    pulseMs: 620,
    step: 95,
  },
};
export const VARIANT_IDS = Object.keys(VARIANTS);

/** A point on the line from the petal's middle toward the mark's centre (t = 0 the petal's middle, 1 the centre). */
function originAt(i, t) {
  const p = parts.petals[i];
  return `${(p.cx + (CX - p.cx) * t) * 100}% ${(p.cy + (CY - p.cy) * t) * 100}%`;
}

export default function PetalMark({
  variant = 'fade',
  size = 120,
  ready = false,
  onFinished,
  glow = true,
  className = '',
  style,
}) {
  const rootRef = useRef(null);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const doneRef = useRef(onFinished);
  doneRef.current = onFinished;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const v = VARIANTS[variant] ?? VARIANTS.fade;
    const petals = [...root.querySelectorAll('[data-petal]')];
    const dotEl = root.querySelector('[data-dot]');
    const anims = [];
    let timer = 0;
    let early = 0;
    let stopped = false;
    const run = (el, kf, opts) => {
      const a = el.animate(kf, { fill: 'forwards', ...opts });
      anims.push(a);
      return a;
    };
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    petals.forEach((el) => {
      el.style.opacity = '0';
    });
    dotEl.style.opacity = '0';

    const finish = ({ inStep = false } = {}) => {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      clearTimeout(timer);
      clearInterval(early);
      if (inStep) {
        // Ready while the petals are still arriving: the dot is the tenth arrival, one gap after the last petal, in the
        // petals' own spring - no pause before it (Sam, 2026-10-22). The last petals finish their own build meanwhile.
        run(
          dotEl,
          [
            { opacity: 0, transform: 'scale(0)' },
            { opacity: 1, transform: 'scale(1.18)', offset: 0.6 },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: v.buildMs ?? 420, easing: OUT },
        );
        const end = glow
          ? run(
              root,
              [{ filter: GLOW_SOFT }, { filter: GLOW, offset: 0.5 }, { filter: GLOW_SOFT }],
              { duration: 1050, easing: 'ease-in-out' },
            )
          : run(root, [{ opacity: 1 }, { opacity: 1 }], { duration: v.buildMs ?? 420 });
        if (v.quick) {
          timer = setTimeout(() => doneRef.current?.(), (v.buildMs ?? 420) + 60);
          return;
        }
        end.finished.then(() => doneRef.current?.()).catch(() => {});
        return;
      }
      // The lap is done: every petal full, then the dot lands - the full stop - with a little glow.
      // Petals still out come up first; when the circle is already complete (a lap that just filled it) the dot lands at
      // once - a full circle waiting for nothing read as a hitch (Sam, P1).
      const lag = petals.map((el) => parseFloat(getComputedStyle(el).opacity));
      petals.forEach((el, i) =>
        run(el, [{ opacity: lag[i] }, { opacity: 1, transform: 'none', filter: NO_GLOW }], {
          duration: 180,
          delay: i * 18,
          easing: OUT,
        }),
      );
      const complete = lag.every((o) => o > 0.97);
      const at = complete ? 30 : 180 + 8 * 18;
      // A loader that just completed its circle lands the dot quicker: what it was covering is ready behind it.
      const dotMs = complete && !glow ? 300 : 420;
      run(
        dotEl,
        [
          { opacity: 0, transform: 'scale(.3)' },
          { opacity: 1, transform: 'scale(1.16)', offset: 0.55 },
          { opacity: 1, transform: 'scale(1)' },
        ],
        { duration: dotMs, delay: at, easing: OUT },
      );
      // The glow swells from the soft glow the build already started, peaks as the dot settles and eases back - one slow
      // breath, not a burst (Sam, 2026-10-20).
      const end = glow
        ? run(root, [{ filter: GLOW_SOFT }, { filter: GLOW, offset: 0.5 }, { filter: GLOW_SOFT }], {
            duration: 1050,
            delay: Math.max(0, at - 60),
            easing: 'ease-in-out',
          })
        : run(root, [{ opacity: 1 }, { opacity: 1 }], { duration: 1, delay: at + dotMs });
      end.finished.then(() => doneRef.current?.()).catch(() => {});
    };

    if (reduce) {
      petals.forEach((el) => run(el, [{ opacity: 1 }], { duration: 1 }));
      finish();
      return () => anims.forEach((a) => a.cancel());
    }

    const cleanup = () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(timer);
      anims.forEach((a) => a.cancel());
    };
    if (v.window) {
      // A few petals at a time going round (Sam, 2026-10-20). Each petal has ONE repeating animation, offset by its place
      // round the circle, so the browser's animation engine runs the group at a fixed pace - a busy page can no longer slow
      // it or make it jump (the ticks of a JS timer did: "it changes its speed"). Ready, every petal that is out lights at
      // the moment it would have come on anyway, so the circle completes at that same pace, then the dot.
      const P = 9 * v.step,
        onMs = v.window * v.step,
        inMs = 200,
        outMs = 320;
      const loopKf = [
        { opacity: 0, offset: 0 },
        { opacity: 1, offset: inMs / P },
        { opacity: 1, offset: onMs / P },
        { opacity: 0, offset: Math.min(1, (onMs + outMs) / P) },
        { opacity: 0, offset: 1 },
      ];
      const t0 = performance.now();
      const loops = petals.map((el, i) =>
        run(el, loopKf, { duration: P, delay: i * v.step, iterations: Infinity, fill: 'both' }),
      );
      timer = setInterval(() => {
        if (!readyRef.current) return;
        clearInterval(timer);
        const elapsed = performance.now() - t0;
        let last = 0;
        petals.forEach((el, i) => {
          // The petal's own clock, not the time since this code ran: a map build holding the main thread starts the loops
          // late, and phases from `elapsed` then lit the wrong petals - the circle jumped instead of filling in turn.
          const clock = loops[i].currentTime ?? elapsed;
          const cur = parseFloat(getComputedStyle(el).opacity) || 0;
          loops[i].cancel();
          const local = clock - i * v.step,
            phase = local < 0 ? null : local % P;
          // on now: stay on (and finish fading up); out: wait for its own slot, then light and stay
          const wait = phase == null ? -local : phase < onMs ? 0 : P - phase;
          run(
            el,
            [
              { opacity: cur, offset: 0 },
              { opacity: cur, offset: wait / (wait + inMs) },
              { opacity: 1, offset: 1 },
            ],
            { duration: wait + inMs, easing: 'linear' },
          );
          last = Math.max(last, wait + inMs);
        });
        timer = setTimeout(finish, last);
      }, 40);
      return cleanup;
    }
    if (v.paced) {
      // The build itself waits: past the fifth petal it slows while the app is not ready, and the last petal waits for it.
      let i = 0;
      const next = () => {
        if (stopped) return;
        if (i === 8 && !readyRef.current) {
          timer = setTimeout(next, 80);
          return;
        }
        run(petals[i], v.build(i, size), { duration: v.buildMs, easing: OUT });
        i += 1;
        if (i > 8) {
          timer = setTimeout(finish, v.buildMs - 120);
          return;
        }
        timer = setTimeout(next, !readyRef.current && i >= 5 ? v.gap * 3 : v.gap);
      };
      next();
      return cleanup;
    }

    // Build: clockwise from 12 o'clock.
    petals.forEach((el, i) =>
      run(el, v.build(i, size), { duration: v.buildMs, delay: i * v.gap, easing: OUT }),
    );
    const built = 8 * v.gap + v.buildMs;
    // A glowing mark starts its glow as the last petals land, so the finish swells out of it instead of switching on.
    if (glow)
      run(root, [{ filter: NO_GLOW }, { filter: GLOW_SOFT }], {
        duration: 700,
        delay: Math.max(0, built - 500),
        easing: 'ease-in',
      });
    const startChase = () => {
      if (readyRef.current) {
        finish();
        return;
      }
      if (v.hold) {
        // no second round: the full mark breathes softly until the app is ready, then the dot and glow
        const breathe = root.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.035)' }], {
          duration: 900,
          iterations: Infinity,
          direction: 'alternate',
          easing: 'ease-in-out',
        });
        timer = setInterval(() => {
          if (readyRef.current) {
            clearInterval(timer);
            breathe.cancel();
            finish();
          }
        }, 60);
        anims.push(breathe);
        return;
      }
      if (v.relap) {
        // not ready after a full circle: clear it and fill it again (Sam, 2026-10-20)
        petals.forEach((el, i) =>
          run(
            el,
            [
              { opacity: 1, transform: 'scale(1)' },
              { opacity: 0, transform: 'scale(.85)' },
            ],
            { duration: 200, delay: i * 12, easing: 'ease-in' },
          ),
        );
        timer = setTimeout(() => {
          if (stopped) return;
          petals.forEach((el, i) =>
            run(el, v.build(i, size), { duration: v.buildMs, delay: i * v.gap, easing: OUT }),
          );
          timer = setTimeout(startChase, built);
        }, 320);
        return;
      }
      // Waiting: settle to the resting level, then chase round until ready; ready ends the lap at the last petal.
      if (!v.startDim && v.rest < 1)
        petals.forEach((el) =>
          run(el, [{ opacity: 1 }, { opacity: v.rest }], { duration: 260, easing: 'ease-out' }),
        );
      let head = 0;
      timer = setInterval(() => {
        const el = petals[head];
        el.animate(v.pulse(head, size), { duration: v.pulseMs, easing: 'ease-in-out' }); // ends at the resting level: nothing to keep
        if (head === 8 && readyRef.current) {
          setTimeout(finish, v.step);
          clearInterval(timer);
        }
        head = (head + 1) % 9;
      }, v.step);
    };
    // From the dot's own slot (one gap after the last petal) until the build is done, a ready app lands the dot in step.
    if (!v.startDim && !v.relap) {
      early = setTimeout(() => {
        early = setInterval(() => {
          if (readyRef.current) finish({ inStep: true });
        }, 30);
        if (readyRef.current) finish({ inStep: true });
      }, 9 * v.gap);
    }
    const t0 = setTimeout(() => {
      clearTimeout(early);
      clearInterval(early);
      startChase();
    }, built);
    return () => {
      stopped = true;
      clearTimeout(t0);
      clearTimeout(early);
      clearInterval(early);
      clearTimeout(timer);
      clearInterval(timer);
      anims.forEach((a) => a.cancel());
    };
  }, [variant, size, glow]);

  return (
    <div
      ref={rootRef}
      className={`petal-mark ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden="true"
    >
      {PETALS.map((src, i) => (
        <img
          key={i}
          data-petal={i}
          src={src}
          alt=""
          draggable={false}
          style={{ transformOrigin: `${parts.petals[i].cx * 100}% ${parts.petals[i].cy * 100}%` }}
        />
      ))}
      <img
        data-dot=""
        src={dot}
        alt=""
        draggable={false}
        style={{ transformOrigin: `${CX * 100}% ${CY * 100}%` }}
      />
    </div>
  );
}
